import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { safelyDetectChanges, unsubscribeAll } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import ProjectLinks from 'src/app/common/project.links';
import { UserInfo } from 'src/app/interfaces';
import { TableCellOnActionEvent, TableConstructorData } from 'src/app/interfaces/tables.interfaces';
import { PaginatableListOfExtendedUsers } from 'src/app/swagger-api';
import { UserTableCreators } from 'src/app/views/table-users-list/adapters/table-creator.enum';
import { TableCreatorFactory } from 'src/app/views/table-users-list/adapters/table-creator.method';
import { ConversationsAdapter } from '../helpers/conversations.adapter';
import { ChatBlockingService } from '../services/chat-blocking.service';
import { ChatDialogsService } from '../services/chat-dialogs.service';

@Component({
  selector: 'app-blocked-users',
  templateUrl: './blocked-users.component.html',
  styleUrls: ['./blocked-users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlockedUsersComponent implements OnInit, OnDestroy {
  public tableData: TableConstructorData;

  public currentPage = 1;
  public totalCount = 0;
  public PAGE_LIMIT: number = AppValues.PAGE_LIMIT;
  public readonly LINK_CHAT: string = ProjectLinks.LINK_CHAT;

  private blockedUsers: UserInfo[];
  private subscriptions$: Subscription[] = [];

  private chatBlockingService = inject(ChatBlockingService);
  private chatDialogsService = inject(ChatDialogsService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  public ngOnInit(): void {
    this.fetchBlockedUsers();
    this.subscribeToUserUnblocked();
    this.subscribeToUserBlockedListUpdatedExternally();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public unBlockUser(event: TableCellOnActionEvent): void {
    const blockedUser: UserInfo = this.blockedUsers.find((u: UserInfo) => u.userId === event.actorId);
    this.chatDialogsService.unBlockUserDialog(() => this.chatBlockingService.unBlockUser(blockedUser));
  }

  public onPageChanged(page: number): void {
    this.currentPage = page;
    this.fetchBlockedUsers();
  }

  private fetchBlockedUsers(): void {
    const offset: number = this.PAGE_LIMIT * (this.currentPage - 1);
    const limit: number = this.PAGE_LIMIT;
    this.chatBlockingService.fetchBlockedUsers(offset, limit).subscribe((rest: PaginatableListOfExtendedUsers) => {
      this.blockedUsers = rest.items.map(ConversationsAdapter.adaptUserBasic2UserInfo);
      this.totalCount = rest.total_count;
      this.synchronizeTableData();
    });
  }

  private subscribeToUserUnblocked(): void {
    this.subscriptions$.push(
      this.chatBlockingService.userUnblocked.subscribe((unblockedUser: UserInfo) => {
        this.blockedUsers = [...this.blockedUsers].filter((user: UserInfo) => user.userId !== unblockedUser.userId);
        if (this.currentPage > 1 && this.blockedUsers.length === 0) {
          this.currentPage = this.currentPage - 1;
        }
        this.fetchBlockedUsers();
      }),
    );
  }

  private synchronizeTableData(): void {
    this.tableData = TableCreatorFactory.createTableConstructorData(UserTableCreators.MANAGE_USER_INFOS, {
      users: this.blockedUsers,
    });
    safelyDetectChanges(this.changeDetectorRef);
  }

  private subscribeToUserBlockedListUpdatedExternally(): void {
    this.subscriptions$.push(
      this.chatBlockingService.userBlockedListChangedExternally.subscribe(() => this.fetchBlockedUsers()),
    );
  }
}
